import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps, forwardRef } from 'react';

import { cn } from '~/utils/cn.ts';

const buttonVariants = cva(
	cn(
		// Base styles - Lyra style: sharp corners, smaller text
		'inline-flex items-center justify-center whitespace-nowrap',
		'rounded-none border border-transparent bg-clip-padding',
		'shrink-0 select-none font-medium text-xs transition-all',
		// Focus styles
		'outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50',
		// Disabled styles
		'disabled:pointer-events-none disabled:opacity-50',
		// Aria states
		'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
		'aria-invalid:border-destructive aria-invalid:ring-1 dark:aria-invalid:border-destructive/50',
		// Icon sizing
		'[&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
	),
	{
		defaultVariants: {
			size: 'default',
			variant: 'default',
		},
		variants: {
			size: {
				default: 'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
				icon: 'size-8',
				'icon-lg': 'size-9',
				'icon-sm': 'size-7',
				'icon-xs': 'size-6 [&_svg:not([class*="size-"])]:size-3',
				lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
				sm: 'h-7 gap-1 px-2.5 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*="size-"])]:size-3.5',
				xs: 'h-6 gap-1 px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*="size-"])]:size-3',
			},
			variant: {
				default: cn('bg-primary text-primary-foreground', 'hover:bg-primary/80'),
				destructive: cn(
					'bg-destructive/10 text-destructive',
					'hover:bg-destructive/20',
					'focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
					'dark:bg-destructive/20 dark:hover:bg-destructive/30',
					'focus-visible:border-destructive/40',
				),
				// Custom variants (project-specific)
				destructive_outline: cn('border-destructive bg-destructive/10 text-destructive', 'hover:bg-destructive/20'),
				ghost: cn(
					'hover:bg-muted hover:text-foreground',
					'dark:hover:bg-muted/50',
					'aria-expanded:bg-muted aria-expanded:text-foreground',
				),
				link: cn('text-primary underline-offset-4 hover:underline'),
				outline: cn(
					'border-border bg-background',
					'hover:bg-muted hover:text-foreground',
					'dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
					'aria-expanded:bg-muted aria-expanded:text-foreground',
				),
				secondary: cn(
					'bg-secondary text-secondary-foreground',
					'hover:bg-secondary/80',
					'aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
				),
			},
		},
	},
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{ className, variant, size, asChild = false, ...props },
	ref,
) {
	const Comp = asChild ? Slot : 'button';
	return <Comp className={cn(buttonVariants({ className, size, variant }))} data-slot="button" ref={ref} {...props} />;
});

export { Button, buttonVariants };
